import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { createPortal } from 'react-dom';
import { deriveSchemaUrlFromReactPageUrl } from './utils.js';
import { ExpandIcon, CloseIcon } from './icons.jsx';
import {
  SIDEBAR_MODAL_MESSAGE_TYPE,
  SIDEBAR_MODAL_CLOSE_MESSAGE_TYPE,
  isSidebarModalRequestPayload,
  buildSidebarModalRequest,
  findKnownSidebarFrame,
  SidebarIframeModalBridge,
} from './sidebar-modal.jsx';
import { App } from './App.jsx';
import { RenderErrorBoundary } from './RenderErrorBoundary.jsx';

export function SidebarApp({ element }) {
  const shouldReduceMotion = useReducedMotion();
  const activeTriggerRef = useRef(null);
  const [sidebarState, setSidebarState] = useState(null);
  const [deleteState, setDeleteState] = useState(null);
  const [batchDeleteState, setBatchDeleteState] = useState(null);
  const [sidebarModalRequest, setSidebarModalRequest] = useState(null);

  function requestCloseSidebarModal(request) {
    if (!request) {
      return false;
    }

    const sourceWindow = request.sourceWindow;
    if (sourceWindow?.CstAdminToolsSidebarModalController?.closeAll) {
      const handled = sourceWindow.CstAdminToolsSidebarModalController.closeAll();
      if (handled) {
        return true;
      }
    }

    setSidebarModalRequest((currentRequest) => {
      if (!currentRequest || currentRequest.sequence !== request.sequence) {
        return currentRequest;
      }

      return null;
    });

    return false;
  }

  useEffect(() => {
    if (!element) {
      return undefined;
    }

    function handleOpen(event) {
      const detail = event.detail || {};
      console.log('[cst_admin_tools] sidebar react received open event', detail);
      const mode = detail.mode === 'edit' ? 'edit' : 'view';
      const pageUrl = detail.pageUrl || '';
      const editPageUrl = detail.editPageUrl || '';
      const fallbackUrl = detail.fallbackUrl || '';
      const editFallbackUrl = detail.editFallbackUrl || '';

      activeTriggerRef.current = detail.trigger instanceof HTMLElement ? detail.trigger : null;
      setDeleteState(null);
      setSidebarModalRequest(null);
      setSidebarState({
        mode,
        label: detail.label || '',
        schemaUrl: detail.schemaUrl || (mode === 'edit' ? '' : deriveSchemaUrlFromReactPageUrl(pageUrl)),
        pageUrl,
        fallbackUrl,
        editSchemaUrl: detail.editSchemaUrl || deriveSchemaUrlFromReactPageUrl(editPageUrl),
        editPageUrl,
        editFallbackUrl,
      });
    }

    function handleDelete(event) {
      const detail = event.detail || {};
      activeTriggerRef.current = detail.trigger instanceof HTMLElement ? detail.trigger : null;
      setDeleteState({
        label: detail.label || '',
        url: detail.url || '',
      });
    }

    function handleBatchDelete(event) {
      const detail = event.detail || {};
      activeTriggerRef.current = detail.trigger instanceof HTMLElement ? detail.trigger : null;
      setBatchDeleteState({
        trigger: detail.trigger,
        count: detail.count || 0,
      });
    }

    element.addEventListener('cst-sidebar:open', handleOpen);
    element.addEventListener('cst-sidebar:delete', handleDelete);
    element.addEventListener('cst-sidebar:batch-delete', handleBatchDelete);

    return () => {
      element.removeEventListener('cst-sidebar:open', handleOpen);
      element.removeEventListener('cst-sidebar:delete', handleDelete);
      element.removeEventListener('cst-sidebar:batch-delete', handleBatchDelete);
    };
  }, [element]);

  useEffect(() => {
    const hasOverlay = Boolean(sidebarState || deleteState || batchDeleteState);
    document.body.classList.toggle('has-cst-crud-sidebar', hasOverlay);

    return () => {
      document.body.classList.remove('has-cst-crud-sidebar');
    };
  }, [sidebarState, deleteState]);

  useEffect(() => {
    if (sidebarState) {
      return undefined;
    }

    setSidebarModalRequest(null);
    return undefined;
  }, [sidebarState]);

  useEffect(() => {
    function handleSidebarModalMessage(event) {
      if (!sidebarState || event.origin !== window.location.origin) {
        return;
      }

      const sourceFrame = findKnownSidebarFrame(event.source);
      if (!(sourceFrame instanceof HTMLIFrameElement)) {
        return;
      }

      if (event.data?.type === SIDEBAR_MODAL_CLOSE_MESSAGE_TYPE) {
        setSidebarModalRequest((currentRequest) => {
          if (!currentRequest) {
            return currentRequest;
          }

          if (currentRequest.sourceWindow && event.source && currentRequest.sourceWindow !== event.source) {
            return currentRequest;
          }

          if (event.data.modalId && currentRequest.modalId && event.data.modalId !== currentRequest.modalId) {
            return currentRequest;
          }

          return null;
        });
        return;
      }

      if (!isSidebarModalRequestPayload(event.data)) {
        return;
      }

      setSidebarModalRequest(buildSidebarModalRequest(event.data, event.source));
    }

    window.addEventListener('message', handleSidebarModalMessage);
    return () => {
      window.removeEventListener('message', handleSidebarModalMessage);
    };
  }, [sidebarState]);

  useEffect(() => {
    const previousBridge = window.CstAdminToolsSidebarModalBridge;

    window.CstAdminToolsSidebarModalBridge = {
      open(payload, sourceWindow) {
        if (!sidebarState || !payload || !isSidebarModalRequestPayload(payload)) {
          return false;
        }

        if (!(findKnownSidebarFrame(sourceWindow) instanceof HTMLIFrameElement)) {
          return false;
        }

        setSidebarModalRequest(buildSidebarModalRequest(payload, sourceWindow));

        return true;
      },
      close(modalId, sourceWindow) {
        setSidebarModalRequest((currentRequest) => {
          if (!currentRequest) {
            return currentRequest;
          }

          if (sourceWindow && currentRequest.sourceWindow && sourceWindow !== currentRequest.sourceWindow) {
            return currentRequest;
          }

          if (modalId && currentRequest.modalId && modalId !== currentRequest.modalId) {
            return currentRequest;
          }

          return null;
        });

        return true;
      },
    };

    return () => {
      if (previousBridge) {
        window.CstAdminToolsSidebarModalBridge = previousBridge;
      } else {
        delete window.CstAdminToolsSidebarModalBridge;
      }
    };
  }, [sidebarState]);

  useEffect(() => {
    if (!sidebarState && !deleteState && !batchDeleteState) {
      return undefined;
    }

    function handleKeyDown(event) {
      if (event.key !== 'Escape') {
        return;
      }

      if (batchDeleteState) {
        setBatchDeleteState(null);
        return;
      }

      if (deleteState) {
        setDeleteState(null);
        return;
      }

      if (sidebarModalRequest) {
        requestCloseSidebarModal(sidebarModalRequest);
        return;
      }

      setSidebarState(null);
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [sidebarModalRequest, sidebarState, deleteState, batchDeleteState]);

  useEffect(() => {
    if (sidebarState || deleteState || batchDeleteState) {
      return undefined;
    }

    const trigger = activeTriggerRef.current;
    if (!(trigger instanceof HTMLElement)) {
      return undefined;
    }

    const frame = window.requestAnimationFrame(() => {
      trigger.focus();
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [sidebarState, deleteState, batchDeleteState]);

  function closeSidebar() {
    if (sidebarModalRequest) {
      requestCloseSidebarModal(sidebarModalRequest);
    }
    setSidebarState(null);
  }

  function confirmDelete() {
    if (!deleteState?.url) {
      return;
    }

    window.location.assign(deleteState.url);
  }

  function confirmBatchDelete() {
    if (!batchDeleteState?.trigger) {
      return;
    }

    const trigger = batchDeleteState.trigger;
    setBatchDeleteState(null);
    trigger.removeAttribute('data-cst-batch-confirm');
    trigger.click();
  }

  function closeSidebarModal(sequence) {
    setSidebarModalRequest((currentRequest) => {
      if (!currentRequest || currentRequest.sequence !== sequence) {
        return currentRequest;
      }

      return null;
    });
  }

  const overlayTransition = shouldReduceMotion
    ? { duration: 0 }
    : { duration: 0.18, ease: [0.22, 1, 0.36, 1] };
  const panelTransition = shouldReduceMotion
    ? { duration: 0 }
    : { type: 'spring', stiffness: 380, damping: 34, mass: 0.85 };
  const portalTarget = typeof document !== 'undefined' ? document.body : null;
  const overlayContent = (
    <>
      <AnimatePresence>
        {sidebarState ? (
          <div className="cat-react-sidebar-layer">
            <motion.button
              key="sidebar-backdrop"
              type="button"
              className="cat-react-sidebar__backdrop"
              aria-label="Close sidebar"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={overlayTransition}
              onClick={closeSidebar}
            />
            <motion.aside
              key="sidebar-panel"
              className="cat-react-sidebar"
              role="dialog"
              aria-modal="true"
              aria-labelledby="cat-react-sidebar-title"
              initial={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, x: 56, scale: 0.98 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, x: 40, scale: 0.985 }}
              transition={panelTransition}
            >
              <header className="cat-react-sidebar__header">
                <div>
                  <p className="cat-react-sidebar__eyebrow">{sidebarState.mode === 'edit' ? 'edit' : 'view'}</p>
                  <h2 className="cat-react-sidebar__title" id="cat-react-sidebar-title">{sidebarState.label}</h2>
                </div>
                <div className="cat-react-sidebar__toolbar">
                  {sidebarState.pageUrl ? (
                    <a
                      className="cat-react-sidebar__toolbar-button"
                      href={sidebarState.pageUrl}
                      aria-label="Plein écran"
                      title="Plein écran"
                    >
                      <ExpandIcon />
                      <span className="cat-react-visually-hidden">Plein écran</span>
                    </a>
                  ) : null}
                  <button
                    type="button"
                    className="cat-react-sidebar__toolbar-button cat-react-sidebar__toolbar-button--ghost"
                    onClick={closeSidebar}
                    aria-label="Fermer"
                    title="Fermer"
                  >
                    <CloseIcon />
                    <span className="cat-react-visually-hidden">Fermer</span>
                  </button>
                </div>
              </header>
              <div className="cat-react-sidebar__body">
                <RenderErrorBoundary message="React sidebar content failed to render.">
                  <App
                    key={`${sidebarState.mode}:${sidebarState.schemaUrl}`}
                    settings={{
                      schemaUrl: sidebarState.schemaUrl,
                      layout: 'sidebar',
                      fallbackUrl: sidebarState.fallbackUrl,
                    }}
                  />
                </RenderErrorBoundary>
              </div>
            </motion.aside>
          </div>
        ) : null}
      </AnimatePresence>

      <AnimatePresence>
        {deleteState ? (
          <div className="cat-react-confirm-layer">
            <motion.button
              key="delete-backdrop"
              type="button"
              className="cat-react-confirm__backdrop"
              aria-label="Fermer la confirmation de suppression"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={overlayTransition}
              onClick={() => setDeleteState(null)}
            />
            <motion.div
              key="delete-panel"
              className="cat-react-confirm"
              role="dialog"
              aria-modal="true"
              aria-labelledby="cat-react-confirm-title"
              initial={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, y: 18, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, y: 12, scale: 0.99 }}
              transition={panelTransition}
            >
              <h2 className="cat-react-confirm__title" id="cat-react-confirm-title">Confirmation de suppression</h2>
              <p className="cat-react-confirm__body">
                {deleteState.label
                  ? `Etes-vous sur de vouloir supprimer "${deleteState.label}" ?`
                  : 'Etes-vous sur de vouloir supprimer cet element ?'}
              </p>
              <div className="cat-react-confirm__actions">
                <button
                  type="button"
                  className="cat-react-button cat-react-button--secondary"
                  onClick={() => setDeleteState(null)}
                >
                  Non
                </button>
                <button type="button" className="cat-react-button cat-react-button--danger" onClick={confirmDelete}>
                  Oui, supprimer
                </button>
              </div>
            </motion.div>
          </div>
        ) : null}
      </AnimatePresence>

      <AnimatePresence>
        {batchDeleteState ? (
          <div className="cat-react-confirm-layer">
            <motion.button
              key="batch-delete-backdrop"
              type="button"
              className="cat-react-confirm__backdrop"
              aria-label="Fermer la confirmation de suppression"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={overlayTransition}
              onClick={() => setBatchDeleteState(null)}
            />
            <motion.div
              key="batch-delete-panel"
              className="cat-react-confirm"
              role="dialog"
              aria-modal="true"
              aria-labelledby="cat-react-batch-confirm-title"
              initial={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, y: 18, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, y: 12, scale: 0.99 }}
              transition={panelTransition}
            >
              <h2 className="cat-react-confirm__title" id="cat-react-batch-confirm-title">Confirmation de suppression</h2>
              <p className="cat-react-confirm__body">
                {batchDeleteState.count > 1
                  ? `Etes-vous sur de vouloir supprimer ces ${batchDeleteState.count} éléments ?`
                  : 'Etes-vous sur de vouloir supprimer cet element ?'}
              </p>
              <div className="cat-react-confirm__actions">
                <button
                  type="button"
                  className="cat-react-button cat-react-button--secondary"
                  onClick={() => setBatchDeleteState(null)}
                >
                  Non
                </button>
                <button type="button" className="cat-react-button cat-react-button--danger" onClick={confirmBatchDelete}>
                  Oui, supprimer
                </button>
              </div>
            </motion.div>
          </div>
        ) : null}
      </AnimatePresence>

      <AnimatePresence>
        {sidebarModalRequest ? (
          <SidebarIframeModalBridge
            key={sidebarModalRequest.sequence}
            request={sidebarModalRequest}
            onClose={() => closeSidebarModal(sidebarModalRequest.sequence)}
          />
        ) : null}
      </AnimatePresence>
    </>
  );

  return (
    <>
      <div className="cat-react-sidebar-debug-anchor" hidden data-cst-sidebar-debug="react-mounted">
        react sidebar mounted
      </div>
      {portalTarget ? createPortal(overlayContent, portalTarget) : null}
    </>
  );
}
